import { parseDate, Time } from '@internationalized/date';
import { ErrorMessage, useFormikContext } from 'formik';

import { CustomErrorMessage } from '@src/design-system/error-message';
import { TimeField } from '@src/design-system/molecules/time-field';

import { useEffect, useState } from 'react';
import type { TAppointmentFormValues } from '@shared/forms/appointment';
import { appointmentFormNames } from '@shared/forms/appointment';
import { Textfield } from '@shared/fields/text-field';
import { SelectField } from '@shared/fields/select-field';
import { APPOINTMENTS_INTERVIEW_TYPES } from '@shared/types/appointment';
import { Label } from '@src/design-system/label';
import { AppointmentDetailsField } from './fields/appointment-details-field';
import { TimezoneField } from '@shared/fields/timezone-field';
import { FormikDatePicker } from '@src/shared/fields/formik-date-picker-field';

const { date, time, name, interviewType, timezone } = appointmentFormNames;

export function SharedAppointmentFields() {
  const [initialValue, setDateInitialValue] = useState<string | null>(null);

  const { setFieldValue, values } = useFormikContext<TAppointmentFormValues>();

  const parseTimeStringToTimeValue = (timeString: string): Time => {
    const [hours, minutes, seconds = '0'] = timeString.split(':');
    return new Time(
      parseInt(hours, 10),
      parseInt(minutes, 10),
      parseInt(seconds, 10)
    );
  };

  useEffect(() => {
    if (values.date && !initialValue) {
      setDateInitialValue(`${values.date}T${values.time}`);
    }
  }, [initialValue, values.date, values.time]);

  return (
    <div className='flex flex-col gap-6'>
      <div>
        <Label>Interview Type</Label>
        <SelectField
          selectPlaceholder='Select an interview type'
          selectName={interviewType}
          options={Object.values(APPOINTMENTS_INTERVIEW_TYPES)}
        />
      </div>

      <div>
        <Textfield
          label='Name'
          name={name}
          placeholder='ex: Work visa application...'
        />
      </div>

      <AppointmentDetailsField />

      <div>
        <FormikDatePicker
          name={date}
          label='Date'
          onChange={(newDate) => setFieldValue(date, newDate?.toString())}
          granularity='day'
          value={values.date ? parseDate(values.date) : undefined}
        />
      </div>

      <div className='flex gap-4'>
        <div className='flex-1'>
          <TimeField
            onChange={(newTime) => setFieldValue(time, newTime?.toString())}
            value={
              values.time ? parseTimeStringToTimeValue(values.time) : undefined
            }
            hideTimeZone
          />
          <ErrorMessage
            name={time}
            component={CustomErrorMessage}
          />
        </div>

        <div className='flex-1'>
          <Label>Timezone</Label>
          <TimezoneField
            selectName={timezone}
            selectPlaceholder='Select a timezone'
          />
        </div>
      </div>
    </div>
  );
}
